import { observer } from 'mobx-react-lite';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '~/components/Modal/Modal';
import { useModal } from '~/hooks/useModal';

import { checkoutStore, userStore } from '~/stores';
import { CUSTOMER_NAME_MIN_LENGTH } from '~/utils/validators';

import useDebounce from '../DebounceHook';
import Icon from '../Icon/Icon';

export default observer(() => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const nameVal = useDebounce<string>(checkoutStore.nameVal, 800);

  const backHandler = () => closeModal();

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    checkoutStore.setIsEmailSync(false);
    checkoutStore.setIsPersonalDataChanged(true);
    checkoutStore.setIsPersonalDataSync(false);
    checkoutStore.setNameVal(e.currentTarget.value);
  };

  const updateUserName = async () => {
    const { firstName, middleName, lastName } =
      userStore.splitFullName(nameVal);
    setIsLoading(true);
    try {
      await userStore.updatePersonalData({
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
      });
      backHandler();
    } catch (errorKey) {
      if (typeof errorKey === 'string') {
        setError(t(errorKey));
      }
    } finally {
      setIsLoading(false);
      checkoutStore.setIsPersonalDataSync(true);
    }
  };

  useEffect(() => {
    checkoutStore.setNameVal(userStore.fullName);
  }, []);

  return (
    <Modal
      isShow={true}
      className="modal-change-name__body"
      onBackdropDismiss={backHandler}
    >
      <div className="popover-container input-form">
        <div className="popover">
          <button
            className="button _no-padding _no-color input-form__close"
            onClick={backHandler}
          >
            <Icon type="plus" className="icon__rotate-45" size={36} />
          </button>
          <button
            className="button _no-padding _no-color input-form__back"
            onClick={backHandler}
          >
            <Icon type="arrow" className="icon__rotate-180" size={24} />
          </button>
          <div className="input-form__form">
            <div className="input-form__form-header">
              <p className="input-form__form-header-title">{t('yourName')}</p>
              <div className="input-form__input-container">
                <input
                  className="input-form__input-visible"
                  placeholder=""
                  value={checkoutStore.nameVal}
                  onChange={handleNameChange}
                  autoFocus
                />
              </div>
              {error && <p className="modal-change-name__error">{error}</p>}
            </div>
            <div className="input-form__form-footer">
              <div className="input-form__form-footer-buttons">
                <button
                  className="button _primary _med"
                  onClick={updateUserName}
                  disabled={
                    isLoading ||
                    checkoutStore.nameVal.trim().length <
                      CUSTOMER_NAME_MIN_LENGTH
                  }
                >
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
});
